// Fretboard visualizer (§5.5): strings × frets with nut, wires, inlays,
// graded string gauges, fret numbers and an instrument label. Active notes are
// accent dots, context notes dark dots, open strings rings at the nut (or on
// the capo). Touch-input mode adds a hover highlight and onPick(string, fret).

import { fingerSingle } from '@simplenotation/theory';
import {
  DOUBLE_INLAYS,
  INLAY_FRETS,
  type FretHit,
  type FretboardGeometry,
  dotRadius,
  fretCenterX,
  fretWidth,
  fretboardGeometry,
  hitTestFretboard,
  notePosition,
  screenX,
} from './fretboard-geometry';
import { CanvasView, dot, fitText, ring, roundRect } from './surface';
import { VIZ_FONT, resolveTheme, sameTheme, type VizTheme } from './theme';
import { DEFAULT_KEY, copyNotes, labelOf, sameNotes, type VizNote, type VizOptionsBase } from './types';

export interface FretboardInstrument {
  /** Sounding MIDI of the open strings, high → low (string 1 first). */
  tuning: readonly number[];
  capo?: number;
  name?: string;
  /** String count (default: tuning length). */
  strings?: number;
  /** Frets drawn (default 24). */
  frets?: number;
}

export interface FretboardOptions extends VizOptionsBase {
  leftHanded: boolean;
  /** 0 = true 12-TET spacing, 1 = even spacing (default 0.45). */
  compress?: number;
}

interface Placed {
  midi: number;
  string: number;
  /** Relative to the capo. */
  fret: number;
  label: string;
}

const DEFAULT_OPTIONS: FretboardOptions = { leftHanded: false, noteNames: 'abc', key: DEFAULT_KEY, touchInput: false };

export class FretboardView extends CanvasView<FretHit> {
  private inst: { tuning: number[]; capo: number; name: string; strings: number; frets: number } = {
    tuning: [64, 59, 55, 50, 45, 40],
    capo: 0,
    name: '',
    strings: 6,
    frets: 24,
  };
  private opts: FretboardOptions = { ...DEFAULT_OPTIONS };
  private theme: VizTheme;
  private active: VizNote[] = [];
  private context: VizNote[] = [];
  private placedActive: Placed[] = [];
  private placedContext: Placed[] = [];
  private geom: FretboardGeometry | null = null;
  private bg: HTMLCanvasElement | null = null;
  private bgValid = false;
  private pickCb: ((string: number, fret: number) => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    super(canvas);
    this.theme = resolveTheme(canvas);
    this.resize();
  }

  setInstrument(inst: FretboardInstrument): void {
    const tuning = [...inst.tuning];
    const strings = Math.max(1, Math.round(inst.strings ?? tuning.length) || 1);
    while (tuning.length < strings) tuning.push(tuning[tuning.length - 1] ?? 40);
    const next = {
      tuning: tuning.slice(0, strings),
      capo: Math.max(0, Math.round(inst.capo ?? 0)),
      name: inst.name ?? '',
      strings,
      frets: Math.max(1, Math.round(inst.frets ?? 24)),
    };
    const cur = this.inst;
    if (
      cur.capo === next.capo &&
      cur.name === next.name &&
      cur.strings === next.strings &&
      cur.frets === next.frets &&
      cur.tuning.length === next.tuning.length &&
      cur.tuning.every((t, i) => t === next.tuning[i])
    )
      return;
    this.inst = next;
    this.geom = null;
    this.bgValid = false;
    this.place();
    this.invalidate();
  }

  /** Cheap when unchanged (safe to call every frame). */
  setNotes(active: readonly VizNote[], context: readonly VizNote[]): void {
    if (sameNotes(active, this.active) && sameNotes(context, this.context)) return;
    this.active = copyNotes(active);
    this.context = copyNotes(context);
    this.place();
    this.invalidate();
  }

  setOptions(o: Partial<FretboardOptions>): void {
    const next: FretboardOptions = { ...this.opts, ...o, key: o.key ?? this.opts.key };
    const prev = this.opts;
    const themeChanged = !sameTheme(prev.theme, next.theme);
    const layoutChanged = prev.leftHanded !== next.leftHanded || prev.compress !== next.compress;
    const labelsChanged = prev.noteNames !== next.noteNames || prev.key.fifths !== next.key.fifths || prev.key.mode !== next.key.mode;
    this.opts = next;
    this.setInteractive(next.touchInput);
    if (!themeChanged && !layoutChanged && !labelsChanged) return;
    if (themeChanged) this.theme = resolveTheme(this.canvas, next.theme);
    if (layoutChanged) this.geom = null;
    this.bgValid = false;
    if (labelsChanged) this.place();
    this.invalidate();
  }

  /** Touch-input callback (fret is relative to the capo). Returns an unsubscribe function. */
  onPick(cb: ((string: number, fret: number) => void) | null): () => void {
    this.pickCb = cb;
    return () => {
      if (this.pickCb === cb) this.pickCb = null;
    };
  }

  override destroy(): void {
    super.destroy();
    this.pickCb = null;
    this.bg = null;
  }

  // ----------------------------------------------------------------------

  private place(): void {
    const { tuning, capo, frets } = this.inst;
    const maxFret = frets - capo;
    const ctx = { tuning, capo, maxFret };
    const placeOne = (n: VizNote): Placed | null => {
      let string = n.string;
      let fret = n.fret;
      if (string !== undefined && fret === undefined && tuning[string - 1] !== undefined) {
        const f = Math.round(n.midi) - tuning[string - 1] - capo;
        fret = f >= 0 && f <= maxFret ? f : undefined;
        if (fret === undefined) string = undefined;
      } else if (string === undefined && fret !== undefined) {
        const s = tuning.findIndex((t) => t + capo + fret! === Math.round(n.midi));
        string = s >= 0 ? s + 1 : undefined;
        if (string === undefined) fret = undefined;
      }
      if (string === undefined || fret === undefined) {
        const p = fingerSingle(Math.round(n.midi), ctx);
        if (!p) return null;
        string = p.string;
        fret = p.fret;
      }
      if (string < 1 || string > this.inst.strings || fret < 0 || fret > maxFret) return null;
      return { midi: n.midi, string, fret, label: labelOf(n, this.opts.noteNames, this.opts.key) };
    };
    this.placedActive = this.active.map(placeOne).filter((p): p is Placed => p !== null);
    const taken = new Set(this.placedActive.map((p) => p.string * 1000 + p.fret));
    this.placedContext = this.context
      .map(placeOne)
      .filter((p): p is Placed => p !== null && !taken.has(p.string * 1000 + p.fret));
  }

  protected override onResize(): void {
    this.geom = null;
    this.bgValid = false;
  }

  private geometry(): FretboardGeometry {
    this.geom ??= fretboardGeometry({
      width: this.width,
      height: this.height,
      strings: this.inst.strings,
      frets: this.inst.frets,
      leftHanded: this.opts.leftHanded,
      compress: this.opts.compress,
    });
    return this.geom;
  }

  protected hitTest(x: number, y: number): FretHit | null {
    if (this.width <= 0) return null;
    return hitTestFretboard(this.geometry(), x, y, this.inst.capo);
  }

  protected sameHit(a: FretHit, b: FretHit): boolean {
    return a.string === b.string && a.fret === b.fret;
  }

  protected activate(hit: FretHit): void {
    this.pickCb?.(hit.string, hit.fret);
  }

  protected paint(c: CanvasRenderingContext2D, w: number, h: number): void {
    const g = this.geometry();
    if (!this.bgValid || !this.bg) this.renderBackground(g);
    c.clearRect(0, 0, w, h);
    if (this.bg) c.drawImage(this.bg, 0, 0, w, h);
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    for (const p of this.placedContext) this.drawNote(c, g, p, false);
    for (const p of this.placedActive) this.drawNote(c, g, p, true);
    if (this.hover && this.interactive) {
      const pos = notePosition(g, this.hover.string, this.hover.fret, this.inst.capo);
      if (pos) {
        const r = dotRadius(g, this.hover.fret + this.inst.capo);
        const x = screenX(g, pos.x);
        c.globalAlpha = 0.25;
        dot(c, x, pos.y, r, this.theme.accent);
        c.globalAlpha = 1;
        ring(c, x, pos.y, r, this.theme.accent, 2);
      }
    }
  }

  private drawNote(c: CanvasRenderingContext2D, g: FretboardGeometry, p: Placed, active: boolean): void {
    const capo = this.inst.capo;
    const pos = notePosition(g, p.string, p.fret, capo);
    if (!pos) return;
    const phys = p.fret + capo;
    const x = screenX(g, pos.x);
    const y = pos.y;
    const r = dotRadius(g, phys);
    const t = this.theme;
    if (p.fret === 0) {
      // Open string: a ring at the nut / on the capo.
      dot(c, x, y, r, active ? t.background : t.context);
      ring(c, x, y, r - 1, active ? t.active : t.fret, active ? 2.5 : 1.5);
      if (p.label) {
        c.fillStyle = active ? t.active : t.contextText;
        fitText(c, p.label, x, y + 0.5, r * 2 - 4);
      }
      return;
    }
    dot(c, x, y, r, active ? t.active : t.context);
    if (!active) {
      c.globalAlpha = 0.6;
      ring(c, x, y, r - 0.5, t.fret, 1);
      c.globalAlpha = 1;
    }
    if (p.label && r >= 6) {
      c.fillStyle = active ? t.activeText : t.contextText;
      fitText(c, p.label, x, y + 0.5, r * 2 - 3);
    }
  }

  private renderBackground(g: FretboardGeometry): void {
    if (typeof document === 'undefined') return;
    this.bg ??= document.createElement('canvas');
    const bg = this.bg;
    const pw = Math.max(1, Math.round(this.width * this.dpr));
    const ph = Math.max(1, Math.round(this.height * this.dpr));
    if (bg.width !== pw) bg.width = pw;
    if (bg.height !== ph) bg.height = ph;
    const c = bg.getContext('2d');
    if (!c) return;
    c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const t = this.theme;
    const { tuning, capo, frets, strings, name } = this.inst;
    const W = this.width;
    const sx = (x: number) => screenX(g, x);
    c.clearRect(0, 0, W, this.height);
    c.fillStyle = t.background;
    c.fillRect(0, 0, W, this.height);

    // Fingerboard.
    const x0 = sx(g.nutX);
    const x1 = sx(g.wireX[frets]);
    c.fillStyle = t.wood;
    c.fillRect(Math.min(x0, x1), g.boardTop, Math.abs(x1 - x0), g.boardBottom - g.boardTop);

    // Inlays.
    c.fillStyle = t.inlay;
    const midY = (g.boardTop + g.boardBottom) / 2;
    for (const f of INLAY_FRETS) {
      if (f > frets) continue;
      const cx = sx(fretCenterX(g, f));
      const r = Math.max(2, Math.min(6, g.spacing * 0.3, fretWidth(g, f) * 0.2));
      if (DOUBLE_INLAYS.includes(f) && strings >= 3) {
        // The octave frets: two dots, a touch larger, so 12 reads at a glance.
        const off = (g.boardBottom - g.boardTop) * 0.25;
        dot(c, cx, midY - off, r * 1.15, t.inlay);
        dot(c, cx, midY + off, r * 1.15, t.inlay);
      } else dot(c, cx, midY, r, t.inlay);
    }

    // Fret wires.
    c.strokeStyle = t.fret;
    c.lineWidth = 2;
    c.beginPath();
    for (let i = 1; i <= frets; i++) {
      const x = Math.round(sx(g.wireX[i])) + 0.5;
      c.moveTo(x, g.boardTop);
      c.lineTo(x, g.boardBottom);
    }
    c.stroke();

    // Nut.
    c.fillStyle = t.nut;
    const nutW = 5;
    c.fillRect(g.leftHanded ? x0 : x0 - nutW, g.boardTop - 1, nutW, g.boardBottom - g.boardTop + 2);

    // Strings: gauge grows with depth of pitch.
    const lo = Math.min(...tuning);
    const hi = Math.max(...tuning);
    const openL = sx(g.openX - 8);
    for (let s = 1; s <= strings; s++) {
      const y = g.stringY[s - 1];
      const depth = hi > lo ? (hi - tuning[s - 1]) / (hi - lo) : 0.5;
      const lw = 0.8 + 2.2 * depth;
      c.strokeStyle = t.string;
      c.lineWidth = lw;
      c.globalAlpha = 0.4;
      c.beginPath();
      c.moveTo(openL, y);
      c.lineTo(x0, y);
      c.stroke();
      c.globalAlpha = 1;
      c.beginPath();
      c.moveTo(x0, y);
      c.lineTo(x1, y);
      c.stroke();
    }

    // Capo.
    if (capo > 0 && capo <= frets) {
      const fw = fretWidth(g, capo);
      const bw = Math.max(5, Math.min(10, fw * 0.35));
      const cx = sx(g.wireX[capo] - fw * 0.3);
      c.fillStyle = t.capo;
      roundRect(c, cx - bw / 2, g.boardTop - 3, bw, g.boardBottom - g.boardTop + 6, 3);
      c.fill();
    }

    // Labels.
    c.fillStyle = t.text;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.font = VIZ_FONT;
    const sys = this.opts.noteNames === 'none' ? 'abc' : this.opts.noteNames;
    c.globalAlpha = 0.75;
    for (let s = 1; s <= strings; s++) {
      const name0 = labelOf({ midi: tuning[s - 1] }, sys, this.opts.key);
      fitText(c, name0, sx(g.tuningX), g.stringY[s - 1] + 0.5, 18);
    }
    c.font = VIZ_FONT;
    const every = (g.wireX[frets] - g.nutX) / frets >= 22;
    for (let f = 1; f <= frets; f++) {
      const marked = INLAY_FRETS.includes(f);
      if (!every && !marked) continue;
      // Marker frets (3, 5, 7, 9, 12…) stand out so the neck can be read without counting.
      c.globalAlpha = marked ? 0.9 : 0.45;
      c.fillText(String(f), sx(fretCenterX(g, f)), g.fretNumberY);
    }
    c.globalAlpha = 0.85;
    const header = [name, capo > 0 ? `Capo ${capo}` : ''].filter(Boolean).join('  ·  ');
    if (header) {
      c.textAlign = g.leftHanded ? 'right' : 'left';
      c.font = VIZ_FONT;
      c.fillText(header, g.leftHanded ? W - 4 : 4, g.headerY);
    }
    c.globalAlpha = 1;
    this.bgValid = true;
  }
}

